import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import RelatedReading from "@/components/RelatedReading";
import SeoMeta from "@/components/SeoMeta";
import ReportWeather from "./ReportWeather";
import {
  ArrowRight, BookOpen, CalendarBlank, Camera, CloudLightning, CloudSun, Drop, Eye,
  Gauge, GraduationCap, Leaf, MapPin, MapTrifold, Mountains, Rainbow, Sun, ThermometerSimple,
  TrendUp, Users, UsersThree, Wind, Windmill,
} from "@phosphor-icons/react";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

const INTRO = `Weather Pulse is NaturePulse's citizen weather network. Together we can observe local conditions, explore weather data, learn about our changing climate, and build a living record of Earth's atmosphere.`;

/* Pill navigation — the mockup's quick-jump row. */
const PILLS = [
  { label: "Live", icon: CloudLightning, to: "/weather-pulse/live", active: true },
  { label: "Today", icon: Sun, to: "/weather-pulse/now" },
  { label: "My City", icon: MapPin, to: "/weather-pulse/journal" },
  { label: "Maps", icon: MapTrifold, to: "/weather-pulse/map" },
  { label: "Forecasts", icon: CalendarBlank, to: "/weather-pulse/journal" },
  { label: "Climate & Trends", icon: TrendUp, to: "/climate-change" },
  { label: "Severe Weather", icon: CloudLightning, to: "/weather-pulse/now" },
  { label: "Air Quality", icon: Wind, to: "/weather-pulse/learn" },
  { label: "Schools & Education", icon: GraduationCap, to: "/weather-pulse/schools" },
  { label: "Learn", icon: BookOpen, to: "/weather-pulse/learn" },
  { label: "Community", icon: UsersThree, to: "/challenges" },
];

/* The 8 feature cards (mockup row). */
const CARDS = [
  { key: "card-current", title: "Current Conditions", copy: "See what's happening now near you.", to: "/weather-pulse/now", tint: "#0284C7", guide: "weather-tab-now" },
  { key: "card-forecasts", title: "Forecasts", copy: "Hourly, daily and extended forecasts.", to: "/weather-pulse/journal", tint: "#0284C7", guide: "weather-tab-forecasts" },
  { key: "card-maps-radar", title: "Maps & Radar", copy: "View radar, satellite and weather maps.", to: "/weather-pulse/map", tint: "#0284C7", guide: "weather-tab-map" },
  { key: "card-seasonal", title: "Seasonal Changes", copy: "Explore seasonal trends in your area.", to: "/weather-pulse/365", tint: "#D97706", guide: "weather-tab-365" },
  { key: "card-severe", title: "Severe Weather", copy: "Alerts, safety tips and resources.", to: "/weather-pulse/now", tint: "#DC2626", guide: "weather-tab-severe" },
  { key: "card-climate", title: "Climate & Trends", copy: "Understand long-term changes.", to: "/climate-change", tint: "#059669", guide: "weather-tab-climate-trends" },
  { key: "card-air-quality", title: "Air Quality", copy: "Monitor air quality and health impacts.", to: "/weather-pulse/learn", tint: "#0284C7", guide: "weather-tab-air-quality" },
  { key: "card-community", title: "Community Science", copy: "Add your observations and join the network.", to: "/weather-pulse/schools", tint: "#059669", guide: "weather-tab-community" },
];

const VALUES = [
  { icon: CloudSun, title: "Local to Global", copy: "Your observations connect to a worldwide network." },
  { icon: Leaf, title: "Real Data, Real People", copy: "Powered by trusted sources and community reports." },
  { icon: TrendUp, title: "A Changing Climate", copy: "Explore trends and learn how we can build a more resilient future." },
  { icon: Users, title: "Stronger Together", copy: "Communities create a clearer picture of our planet." },
];

const FOOTER_PROPS = [
  { icon: Mountains, label: "Healthier Communities" },
  { icon: Rainbow, label: "A Safer Planet" },
  { icon: Windmill, label: "Climate Solutions" },
  { icon: UsersThree, label: "People Make a Difference" },
];

export default function WeatherPulseHome() {
  const [loc, setLoc] = useState(readLocation);
  const [weather, setWeather] = useState(null);
  const [now, setNow] = useState(null);
  const [art, setArt] = useState({});

  useEffect(() => {
    const onChange = () => setLoc(readLocation());
    window.addEventListener("np:location-changed", onChange);
    http.get("/docket-art/weatherpulse").then(({ data }) => setArt(data.art || {})).catch(() => {});
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  useEffect(() => {
    let alive = true;
    http.get("/weather-pulse/now").then(({ data }) => { if (alive) setNow(data); }).catch(() => {});
    if (!loc?.lat) return () => { alive = false; };
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${loc.lat}&longitude=${loc.lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m,pressure_msl&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,uv_index_max&timezone=auto`)
      .then((r) => r.json())
      .then((d) => { if (alive) setWeather(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [loc?.lat, loc?.lon]);

  const cur = weather?.current;
  const daily = weather?.daily;
  const place = [loc?.city, loc?.state].filter(Boolean).join(", ") || loc?.country || null;

  const scrollToReport = () => document.getElementById("wp-report")?.scrollIntoView({ behavior: "smooth" });

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen bg-white text-[#0C2D48]" data-testid="wp-home">
      <SeoMeta
        title="Weather Pulse — Observe. Record. Understand the Weather."
        description="Weather Pulse is NaturePulse's citizen weather network: live conditions, one-tap weather reports, local journals, school stations and a living record of Earth's atmosphere."
        canonical={`${origin}/weather-pulse`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Weather Pulse", url: `${origin}/weather-pulse` }}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pt-32 md:pt-36" data-testid="wp-hero">
        {art.hero && (
          <div className="absolute inset-0">
            <img src={abs(art.hero)} alt="Dramatic sky with sunbeams and clouds over a green landscape" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/25" />
          </div>
        )}
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 pb-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
            <div>
              <div className="flex items-center gap-2">
                <span className="relative inline-flex">
                  <Sun size={44} weight="fill" className="text-[#F59E0B]" />
                  <CloudSun size={30} weight="fill" className="text-[#0284C7] absolute -bottom-1 -right-3" />
                </span>
                <Leaf size={24} weight="fill" className="text-[#16A34A] mt-5" />
              </div>
              <h1 className="mt-3 font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight" data-testid="wp-title">
                <span className="text-[#0284C7]">Weather</span><span className="text-[#16A34A]">Pulse</span>
                <sup className="text-lg text-[#0284C7]/60">™</sup>
              </h1>
              <p className="mt-2 font-serif-display text-xl md:text-2xl text-[#0C2D48]" data-testid="wp-tagline">
                Observe. Record. Understand the Weather.
              </p>
              <p className="mt-4 max-w-xl text-base text-[#374151] leading-relaxed" data-testid="wp-intro">{INTRO}</p>
              <ListenTranslateBar text={INTRO} testidPrefix="wp-home-tools" />
              <div className="mt-6"><PageGuide pageKey="weather:home" testidPrefix="wp-home" /></div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link to="/weather-pulse/now" data-testid="wp-check-today"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#0369A1] transition-colors">
                  Check Today's Weather <ArrowRight size={14} weight="bold" />
                </Link>
                <button onClick={scrollToReport} data-testid="wp-add-observation"
                  className="inline-flex items-center gap-2 rounded-full border border-[#0284C7]/40 bg-white/80 px-6 py-3.5 text-sm font-medium text-[#0284C7] hover:bg-[#F0F9FF] transition-colors">
                  <Camera size={15} weight="bold" /> Add a Weather Observation
                </button>
                <span className="font-serif-display italic text-[#0369A1]/80 text-lg leading-tight">
                  Local Observations.<br />Global Impact.
                </span>
              </div>
            </div>

            {/* Live weather card */}
            <div className="rounded-3xl bg-white/95 backdrop-blur border border-black/5 shadow-xl p-6" data-testid="wp-current">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0C2D48]">
                  <MapPin size={15} weight="fill" className="text-[#0284C7]" />
                  {place || "Your area"}
                </div>
                <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">Live conditions</span>
              </div>
              {cur ? (
                <>
                  <div className="mt-4 flex items-center gap-4">
                    <CloudSun size={52} weight="fill" className="text-[#F59E0B]" />
                    <div>
                      <div className="font-serif-display text-5xl leading-none text-[#0C2D48]" data-testid="wp-temp">{Math.round(cur.temperature_2m)}°C</div>
                      <div className="mt-1 text-sm text-[#6B7280]">Feels like {Math.round(cur.apparent_temperature)}°</div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#374151]">
                    <span className="inline-flex items-center gap-1.5"><Drop size={13} weight="fill" className="text-[#0284C7]" /> Humidity <b className="ml-auto">{cur.relative_humidity_2m}%</b></span>
                    <span className="inline-flex items-center gap-1.5"><Wind size={13} weight="fill" className="text-[#0284C7]" /> Wind <b className="ml-auto">{Math.round(cur.wind_speed_10m)} km/h</b></span>
                    <span className="inline-flex items-center gap-1.5"><Gauge size={13} weight="fill" className="text-[#0284C7]" /> Pressure <b className="ml-auto">{Math.round(cur.pressure_msl)} hPa</b></span>
                    {daily && (
                      <span className="inline-flex items-center gap-1.5"><ThermometerSimple size={13} weight="fill" className="text-[#0284C7]" /> Hi / Lo <b className="ml-auto">{Math.round(daily.temperature_2m_max?.[0])}° / {Math.round(daily.temperature_2m_min?.[0])}°</b></span>
                    )}
                    {daily?.uv_index_max?.[0] != null && (
                      <span className="inline-flex items-center gap-1.5 col-span-2"><Eye size={13} weight="fill" className="text-[#0284C7]" /> UV index <b className="ml-auto">{Math.round(daily.uv_index_max[0])}</b></span>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-6 text-sm text-[#6B7280]">
                  {loc?.lat ? "Loading current conditions…" : "Set your location to see live conditions here."}
                </div>
              )}
              <div className="mt-5 flex gap-2">
                <Link to="/weather-pulse/journal" data-testid="wp-full-forecast"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#16A34A] px-4 py-2.5 text-xs font-medium text-white hover:bg-[#15803D] transition-colors">
                  View Full Forecast <ArrowRight size={12} weight="bold" />
                </Link>
                <button onClick={scrollToReport} data-testid="wp-card-add-obs"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-black/10 px-4 py-2.5 text-xs font-medium text-[#0C2D48] hover:bg-black/5 transition-colors">
                  <Camera size={12} weight="bold" /> Add Observation
                </button>
              </div>
              <div className="mt-3 text-center font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#9CA3AF]">Forecast data · Open-Meteo</div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- PILL NAV ---------------- */}
      <nav className="max-w-[1200px] mx-auto px-5 md:px-10 mt-4" data-testid="wp-pill-nav">
        <div className="flex flex-wrap gap-2">
          {PILLS.map((p) => (
            <Link key={p.label} to={p.to} data-testid={`wp-pill-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                p.active ? "bg-[#166534] text-white" : "border border-black/10 bg-white text-[#0C2D48] hover:border-[#0284C7]/50 hover:bg-[#F0F9FF]"}`}>
              <p.icon size={13} weight="fill" /> {p.label}
            </Link>
          ))}
        </div>
      </nav>

      {/* ---------------- 8 FEATURE CARDS ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-8" data-testid="wp-feature-cards">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CARDS.map((c) => (
            <div key={c.key}>
              <Link to={c.to} data-testid={`wp-card-${c.key.replace("card-", "")}`}
                className="group block rounded-2xl border border-black/5 bg-white shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all">
                {art[c.key] ? (
                  <img src={abs(art[c.key])} alt={c.title} className="h-28 w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-28 w-full" style={{ background: `linear-gradient(135deg, ${c.tint}22, ${c.tint}08)` }} />
                )}
                <div className="p-3.5">
                  <div className="font-semibold text-sm text-[#0C2D48]">{c.title}</div>
                  <p className="mt-1 text-xs text-[#4B5563] leading-snug">{c.copy}</p>
                  <span className="mt-2 inline-flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: `${c.tint}18` }}>
                    <ArrowRight size={10} weight="bold" style={{ color: c.tint }} />
                  </span>
                </div>
              </Link>
              <Link to={`/read/articles/${c.guide}`} data-testid={`wp-card-guide-${c.key.replace("card-", "")}`}
                className="mt-1.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                AI guide →
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- HAPPENING NOW ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10" data-testid="wp-now-strip">
        <div className="rounded-3xl border border-black/5 bg-[#F0F9FF]/60 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#0369A1]">
              Weather happening now · last 24 h
            </div>
            <Link to="/weather-pulse/now" data-testid="wp-now-link" className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#0284C7] hover:underline">
              Full live view <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          {now && now.total > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {now.by_condition.map((c) => (
                <span key={c.condition} data-testid={`wp-now-chip-${c.condition}`} className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3 py-1.5 text-sm">
                  {c.emoji} {c.label} <b className="font-mono-tech text-[10px]">{c.count}</b>
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-[#4B5563]">
              No community reports in the last 24 hours. Yours could be the first — add an observation below.
            </p>
          )}
        </div>
      </section>

      {/* ---------------- REPORT ---------------- */}
      <section id="wp-report" className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10 scroll-mt-32" data-testid="wp-report-section">
        <ReportWeather onReported={() => http.get("/weather-pulse/now").then(({ data }) => setNow(data)).catch(() => {})} />
      </section>

      {/* ---------------- WEATHER-READY BAND ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-12" data-testid="wp-ready-band">
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="rounded-3xl border border-black/5 bg-white shadow-sm overflow-hidden flex flex-col">
            {art["band-ready"] ? (
              <img src={abs(art["band-ready"])} alt="A child observing a weather station" className="h-44 w-full object-cover" loading="lazy" />
            ) : (
              <div className="h-44 w-full bg-gradient-to-br from-[#DBEAFE] to-[#F0F9FF]" />
            )}
            <div className="p-5 flex-1 flex flex-col">
              <div className="font-serif-display text-2xl text-[#0C2D48]">A Weather-Ready Generation</div>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed flex-1">
                From curious kids to lifelong learners, Weather Pulse helps everyone understand the
                atmosphere and its impact on our lives.
              </p>
              <Link to="/weather-pulse/learn" data-testid="wp-learn-all-ages"
                className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#166534] px-5 py-2.5 text-xs font-medium text-white hover:bg-[#14532D] transition-colors">
                Learn for All Ages <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="rounded-3xl border border-black/5 bg-white shadow-sm p-6 flex flex-col justify-center gap-5">
            {VALUES.map((v) => (
              <div key={v.title} className="flex items-start gap-3">
                <v.icon size={24} weight="duotone" className="text-[#0284C7] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm text-[#0C2D48]">{v.title}</div>
                  <div className="text-xs text-[#6B7280] leading-snug">{v.copy}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-black/5 shadow-sm min-h-[260px]">
            {art["band-quote"] ? (
              <img src={abs(art["band-quote"])} alt="Mountain valley filled with morning clouds" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#0C4A6E] to-[#0284C7]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <blockquote className="absolute bottom-0 p-5 text-white" data-testid="wp-quote">
              <p className="font-serif-display italic text-lg leading-snug">"The atmosphere is a shared resource — its story belongs to all of us."</p>
              <footer className="mt-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-white/70">— Sylvia Earle</footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10">
        <RelatedReading tag="weather-pulse" testid="wp-related" />
      </section>

      {/* ---------------- FOOTER BAND ---------------- */}
      <section className="mt-12 border-t border-black/5 bg-gradient-to-b from-white to-[#F0FDF4]" data-testid="wp-footer-band">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="font-serif-display italic text-xl text-[#166534]">Weather Connects Us All.</div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {FOOTER_PROPS.map((f) => (
                <span key={f.label} className="inline-flex items-center gap-2 text-sm text-[#0C2D48]">
                  <f.icon size={20} weight="duotone" className="text-[#16A34A]" /> {f.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
